// skipcq: JS-W1028
import {
  collection,
  doc,
  query,
  where,
  orderBy,
  getDocs,
  setDoc,
  serverTimestamp,
  limit,
} from 'firebase/firestore';
import { deleteObject, ref, uploadBytes } from 'firebase/storage';

import { auth, db, storage } from '../utils/firebase';

export const BREWERY_CLAIMS_COLLECTION = 'breweryClaims';
export const BREWERY_CLAIM_DOCUMENTS_PREFIX = 'breweryClaimDocuments';

export const BREWERY_CLAIM_MAX_FILE_BYTES = 10 * 1024 * 1024;

export const BREWERY_CLAIM_ALLOWED_CONTENT_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
];

/** MIME list for `<input type="file" accept="…">`; keep in sync with `BREWERY_CLAIM_ALLOWED_CONTENT_TYPES`. */
export const BREWERY_CLAIM_PROOF_ACCEPT = BREWERY_CLAIM_ALLOWED_CONTENT_TYPES.join(',');

/** Short helper shown next to the brewery claim proof upload control. */
export const BREWERY_CLAIM_PROOF_HELPER_TEXT = `PDF, JPEG, or PNG · max ${
  BREWERY_CLAIM_MAX_FILE_BYTES / (1024 * 1024)
} MB`;

function ignoreBestEffortDeleteFailure() {
  return undefined;
}

/**
 * @returns {Promise<Array<{
 *   id: string,
 *   userId: string,
 *   breweryId: string,
 *   status: string,
 *   breweryName?: string,
 *   breweryCity?: string,
 *   breweryState?: string,
 *   proofStoragePath?: string,
 *   proofFileName?: string,
 *   createdAt?: import('firebase/firestore').Timestamp,
 * }>>}
 */
export async function fetchBreweryClaimsForCurrentUser() {
  const user = auth.currentUser;
  if (!user) {
    return [];
  }
  const snapshot = await getDocs(
    query(
      collection(db, BREWERY_CLAIMS_COLLECTION),
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc'),
    ),
  );
  return snapshot.docs.map((snapshotDoc) => ({
    id: snapshotDoc.id,
    ...snapshotDoc.data(),
  }));
}

/**
 * All claims for global admin review (newest first). Caller must be a global admin.
 * @returns {Promise<Array<Record<string, unknown> & { id: string }>>}
 */
export async function fetchAllBreweryClaimsForAdmin() {
  const snapshot = await getDocs(
    query(collection(db, BREWERY_CLAIMS_COLLECTION), orderBy('createdAt', 'desc')),
  );
  return snapshot.docs.map((snapshotDoc) => ({
    id: snapshotDoc.id,
    ...snapshotDoc.data(),
  }));
}

/**
 * Claims for one brewery (global admin; used on Engage brewery detail).
 * @param {string} breweryId
 * @returns {Promise<Array<Record<string, unknown> & { id: string }>>}
 */
export async function fetchBreweryClaimsForBrewery(breweryId) {
  if (typeof breweryId !== 'string' || breweryId.trim().length === 0) {
    return [];
  }
  const snapshot = await getDocs(
    query(
      collection(db, BREWERY_CLAIMS_COLLECTION),
      where('breweryId', '==', breweryId.trim()),
      orderBy('createdAt', 'desc'),
    ),
  );
  return snapshot.docs.map((snapshotDoc) => ({
    id: snapshotDoc.id,
    ...snapshotDoc.data(),
  }));
}

/**
 * @param {string} userId
 * @param {string} breweryId
 * @returns {Promise<'none' | 'pending' | 'approved'>}
 */
export async function findBlockingClaimStatus(userId, breweryId) {
  const blocking = await getDocs(
    query(
      collection(db, BREWERY_CLAIMS_COLLECTION),
      where('userId', '==', userId),
      where('breweryId', '==', breweryId),
      where('status', 'in', ['pending', 'approved']),
      limit(5),
    ),
  );
  if (blocking.empty) {
    return 'none';
  }
  let hasPending = false;
  for (const snapshotDoc of blocking.docs) {
    const status = snapshotDoc.data().status;
    if (status === 'approved') {
      return 'approved';
    }
    if (status === 'pending') {
      hasPending = true;
    }
  }
  return hasPending ? 'pending' : 'none';
}

function sanitizeUploadFileName(originalName) {
  const base = typeof originalName === 'string' ? originalName.trim() : '';
  const stripped = base.replace(/^.*[/\\]/u, '').replace(/[^\w.\-()+ ]/gu, '_');
  return stripped.length > 0 ? stripped : 'document';
}

/**
 * @param {{
 *   breweryId: string,
 *   breweryName: string,
 *   breweryCity: string,
 *   breweryState: string,
 *   proofFile: File,
 * }} claimInput
 */
export async function createBreweryClaimWithProofUpload(claimInput) {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('You must be signed in to submit a claim.');
  }

  if (claimInput.proofFile.size > BREWERY_CLAIM_MAX_FILE_BYTES) {
    throw new Error(`File must be at most ${BREWERY_CLAIM_MAX_FILE_BYTES / (1024 * 1024)} MB.`);
  }
  if (!BREWERY_CLAIM_ALLOWED_CONTENT_TYPES.includes(claimInput.proofFile.type)) {
    throw new Error('Allowed file types: PDF, JPEG, PNG.');
  }

  const blockState = await findBlockingClaimStatus(user.uid, claimInput.breweryId);
  if (blockState !== 'none') {
    const err = new Error('DUPLICATE_CLAIM');
    err.code = 'DUPLICATE_CLAIM';
    err.blockingStatus = blockState;
    throw err;
  }

  const claimRef = doc(collection(db, BREWERY_CLAIMS_COLLECTION));
  const claimId = claimRef.id;
  const safeFileName = sanitizeUploadFileName(claimInput.proofFile.name);
  const storagePath = `${BREWERY_CLAIM_DOCUMENTS_PREFIX}/${user.uid}/${claimId}/${safeFileName}`;
  const storageRef = ref(storage, storagePath);

  await uploadBytes(storageRef, claimInput.proofFile, {
    contentType: claimInput.proofFile.type,
  });

  const requesterName =
    typeof user.displayName === 'string' && user.displayName.trim().length > 0
      ? user.displayName.trim()
      : '';
  const requesterEmail =
    typeof user.email === 'string' && user.email.trim().length > 0 ? user.email.trim() : '';

  try {
    await setDoc(claimRef, {
      userId: user.uid,
      breweryId: claimInput.breweryId,
      status: 'pending',
      breweryName: claimInput.breweryName,
      breweryCity: claimInput.breweryCity,
      breweryState: claimInput.breweryState,
      proofStoragePath: storagePath,
      proofFileName: claimInput.proofFile.name,
      proofContentType: claimInput.proofFile.type,
      requesterEmail,
      requesterName,
      createdAt: serverTimestamp(),
    });
  } catch (writeError) {
    await deleteObject(storageRef).catch(ignoreBestEffortDeleteFailure);
    throw writeError;
  }

  return { claimId };
}
